import React, { useState, useRef } from 'react';
import { X, Camera, User, Plug, UserPlus, Check, AlertCircle } from 'lucide-react';
import C from '../../constants/colors';
import * as usersApi from '../../api/users';

function resizeImage(file, maxPx = 300) {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function EmployeeFormModal({ employee, companies, sites, departments, roles, schedules = [], onSave, onClose, preselectedCompanyId, preselectedSiteId }) {
  const [form, setForm] = useState({
    companyId: employee?.companyId || preselectedCompanyId || (companies[0]?.id || ''),
    homeSiteId: employee?.homeSiteId || preselectedSiteId || '',
    name: employee?.name || '',
    employeeId: employee?.employeeId || '',
    email: employee?.email || '',
    departmentId: employee?.departmentId || '',
    role: employee?.role || 'STAFF',
    phone: employee?.phone || '',
    emergencyContact: employee?.emergencyContact || '',
    emergencyPhone: employee?.emergencyPhone || '',
    teamSize: employee?.teamSize || 1,
    teamMembers: (employee?.teamMembers || []).join(', '),
    allowedOtherCompanies: employee?.allowedOtherCompanies || false,
    // Default ON for new employees; preserve existing value when editing
    incidentReportingEnabled: employee ? (employee.incidentReportingEnabled !== false) : true,
    photoUrl: employee?.photoUrl || null,
    taEnabled: employee?.taEnabled || false,
    taSelfieRequired: employee?.taSelfieRequired || false,
    taGeofenceRequired: employee?.taGeofenceRequired || false,
    taScheduleId: employee?.taScheduleId || '',
    taRotationStart: employee?.taRotationStart || '',
  });
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  // Create-Login flow state (promote employee → user)
  const [loginOpen, setLoginOpen] = useState(false);     // toggles the inline password section
  const [tempPassword, setTempPassword] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginSuccessEmail, setLoginSuccessEmail] = useState(''); // shows confirmation after success
  const isFromOdoo = !!employee?.odooId;
  // When synced from Odoo: name, company, phone, Badge ID, department come from Odoo and can't be edited here.
  const required = isFromOdoo
    ? ['role','homeSiteId','emergencyContact','emergencyPhone']
    : ['name','employeeId','departmentId','role','companyId','homeSiteId','phone','emergencyContact','emergencyPhone'];
  const filteredSites = sites.filter(s => s.companyId === form.companyId);
  const filteredDepts = (departments || []).filter(d => d.companyId === form.companyId && d.siteId === form.homeSiteId);
  const filteredSchedules = schedules.filter(s => s.siteId === form.homeSiteId);

  async function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const dataUrl = await resizeImage(file, 300);
    setForm(p => ({ ...p, photoUrl: dataUrl }));
  }

  async function handleSubmit() {
    const missing = required.filter(k => !String(form[k]).trim());
    if (missing.length) { setErrors(missing); return; }
    setSaving(true);
    try {
      const selectedDept = (departments || []).find(d => d.id === form.departmentId);
      await onSave({
        ...(employee || {}),
        companyId: form.companyId, homeSiteId: form.homeSiteId,
        name: form.name.trim(), employeeId: form.employeeId.trim(),
        email: form.email.trim() || null,
        departmentId: form.departmentId || null,
        department: selectedDept?.name || '',
        role: form.role,
        phone: form.phone.trim(), emergencyContact: form.emergencyContact.trim(),
        emergencyPhone: form.emergencyPhone.trim(),
        teamSize: parseInt(form.teamSize) || 1,
        teamMembers: form.teamMembers.split(',').map(s=>s.trim()).filter(Boolean),
        allowedOtherCompanies: form.allowedOtherCompanies,
        incidentReportingEnabled: form.incidentReportingEnabled,
        photoUrl: form.photoUrl,
        taEnabled: form.taEnabled,
        taSelfieRequired: form.taSelfieRequired,
        taGeofenceRequired: form.taGeofenceRequired,
        taScheduleId: form.taScheduleId || null,
        taRotationStart: form.taRotationStart || null,
      });
      onClose();
    } catch (err) {
      setErrors([err?.response?.data?.error || err.message || 'Save failed']);
      setSaving(false);
    }
  }

  // Create a login user for this employee using the email on the form.
  // The employee row is saved first so the email is persisted, then a user
  // is created (POST /api/users) linking back to the employee. Admin-set
  // temporary password; user is created as OPERATOR.
  async function handleCreateLogin() {
    setLoginError('');
    const emailTrimmed = form.email.trim();
    if (!employee?.id) { setLoginError('Save the employee first.'); return; }
    if (!emailTrimmed)  { setLoginError('Add an email above first.');  return; }
    if (tempPassword.length < 8) { setLoginError('Password must be at least 8 characters.'); return; }

    setLoginBusy(true);
    try {
      // Persist any unsaved form changes (including email) so the employee row stays in sync
      const selectedDept = (departments || []).find(d => d.id === form.departmentId);
      await onSave({
        ...(employee || {}),
        companyId: form.companyId, homeSiteId: form.homeSiteId,
        name: form.name.trim(), employeeId: form.employeeId.trim(),
        email: emailTrimmed,
        departmentId: form.departmentId || null,
        department: selectedDept?.name || '',
        role: form.role,
        phone: form.phone.trim(), emergencyContact: form.emergencyContact.trim(),
        emergencyPhone: form.emergencyPhone.trim(),
        teamSize: parseInt(form.teamSize) || 1,
        teamMembers: form.teamMembers.split(',').map(s => s.trim()).filter(Boolean),
        allowedOtherCompanies: form.allowedOtherCompanies,
        incidentReportingEnabled: form.incidentReportingEnabled,
        photoUrl: form.photoUrl,
        taEnabled: form.taEnabled,
        taSelfieRequired: form.taSelfieRequired,
        taGeofenceRequired: form.taGeofenceRequired,
        taScheduleId: form.taScheduleId || null,
        taRotationStart: form.taRotationStart || null,
      });

      await usersApi.createUser({
        employeeRecordId: employee.id,
        email: emailTrimmed.toLowerCase(),
        password: tempPassword,
        role: 'OPERATOR',
      });

      setLoginSuccessEmail(emailTrimmed.toLowerCase());
      setTempPassword('');
      setLoginOpen(false);
    } catch (err) {
      setLoginError(err?.response?.data?.error || err.message || 'Failed to create login');
    } finally {
      setLoginBusy(false);
    }
  }

  const inp = (key, label, ph='', locked=false) => (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}{required.includes(key)?' *':''}
        {locked && <span style={{ marginLeft: 6, fontSize: '9px', color: C.amber, fontWeight: 700, padding: '1px 6px', background: C.amber+'22', borderRadius: '8px', letterSpacing: '0.04em' }}>ODOO</span>}
      </label>
      <input value={form[key]} onChange={e => setForm(p => ({...p,[key]:e.target.value}))} placeholder={ph} disabled={locked}
        style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', opacity: locked ? 0.6 : 1, cursor: locked ? 'not-allowed' : 'text' }} />
    </div>
  );

  const sel = (key, label, opts) => (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}{required.includes(key)?' *':''}</label>
      <select value={form[key]} onChange={e => setForm(p => ({...p,[key]:e.target.value}))}
        style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
        {opts.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '28px', width: '540px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: C.textPrimary }}>{employee ? 'Edit Employee' : 'Add Employee'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}><X size={18} /></button>
        </div>
        {errors.length > 0 && (
          <div style={{ background: C.red+'22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: C.red }}>
            {required.includes(errors[0]) ? `Missing required fields: ${errors.join(', ')}` : errors[0]}
          </div>
        )}

        {isFromOdoo && (
          <div style={{ background: C.amber+'18', border: `1px solid ${C.amber}55`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: C.textSecondary, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <Plug size={14} color={C.amber} style={{ marginTop: '1px', flexShrink: 0 }} />
            <div>
              <div style={{ color: C.amber, fontWeight: 700, marginBottom: '2px' }}>Synced from Odoo</div>
              Name, company, phone, Badge ID and department are managed in Odoo. Other fields can be edited here.
            </div>
          </div>
        )}

        {/* Photo upload */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', padding: '14px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: C.surfaceElevated, border: `2px solid ${C.border}`, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {form.photoUrl
              ? <img src={form.photoUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <User size={24} color={C.textMuted} />}
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary, marginBottom: '4px' }}>Profile Photo</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={() => fileRef.current.click()} style={{ padding: '5px 12px', borderRadius: '7px', border: `1px solid ${C.border}`, background: C.surfaceElevated, color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Camera size={12} />{form.photoUrl ? 'Change Photo' : 'Upload Photo'}
              </button>
              {form.photoUrl && (
                <button type="button" onClick={() => setForm(p => ({ ...p, photoUrl: null }))} style={{ padding: '5px 10px', borderRadius: '7px', border: `1px solid ${C.red}44`, background: C.red+'11', color: C.red, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>
                  Remove
                </button>
              )}
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
          <div style={{ gridColumn: '1/-1' }}>{inp('name','Full Name','John Smith', isFromOdoo)}</div>
          {inp('employeeId','Employee ID / Badge','EMP-001', isFromOdoo)}
          {sel('role','Role',(roles || []).map(r => [r.name, r.name]))}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Company *
              {isFromOdoo && <span style={{ marginLeft: 6, fontSize: '9px', color: C.amber, fontWeight: 700, padding: '1px 6px', background: C.amber+'22', borderRadius: '8px', letterSpacing: '0.04em' }}>ODOO</span>}
            </label>
            <select value={form.companyId} onChange={e => setForm(p => ({...p, companyId: e.target.value, homeSiteId: '', departmentId: ''}))} disabled={isFromOdoo}
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', opacity: isFromOdoo ? 0.6 : 1, cursor: isFromOdoo ? 'not-allowed' : 'pointer' }}>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Home Site *</label>
            <select value={form.homeSiteId} onChange={e => setForm(p => ({...p, homeSiteId: e.target.value, departmentId: ''}))}
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', marginBottom: '14px' }}>
              <option value=''>Select site...</option>
              {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Department{!isFromOdoo && ' *'}
              {isFromOdoo && <span style={{ marginLeft: 6, fontSize: '9px', color: C.amber, fontWeight: 700, padding: '1px 6px', background: C.amber+'22', borderRadius: '8px', letterSpacing: '0.04em' }}>ODOO</span>}
            </label>
            {isFromOdoo ? (
              <input value={employee?.department || '—'} disabled
                style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', opacity: 0.6, cursor: 'not-allowed' }} />
            ) : (
              <select value={form.departmentId} onChange={e => setForm(p => ({...p, departmentId: e.target.value}))}
                disabled={!form.homeSiteId}
                style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: form.homeSiteId ? C.textPrimary : C.textMuted, fontSize: '13px', fontFamily: 'inherit', outline: 'none', opacity: form.homeSiteId ? 1 : 0.5 }}>
                <option value=''>Select department...</option>
                {filteredDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
          </div>
          {inp('phone','Phone','+971-50-000-0000', isFromOdoo)}
          {inp('email','Email (optional)','sarah@acme.com')}
          {inp('emergencyContact','Emergency Contact','Jane Smith')}
          {inp('emergencyPhone','Emergency Phone','+971-50-000-1111')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>Allow access to other companies</div>
            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>Employee can scan into sites belonging to other companies within the same organization</div>
          </div>
          <div onClick={() => setForm(p => ({...p, allowedOtherCompanies: !p.allowedOtherCompanies}))}
            style={{ width: '42px', height: '24px', borderRadius: '12px', background: form.allowedOtherCompanies ? C.blue : C.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: '12px' }}>
            <div style={{ position: 'absolute', top: '3px', left: form.allowedOtherCompanies ? '21px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>Incident Reporting</div>
            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>Show "Report Incident" in the Other menu when employee scans their badge</div>
          </div>
          <div onClick={() => setForm(p => ({...p, incidentReportingEnabled: !p.incidentReportingEnabled}))}
            style={{ width: '42px', height: '24px', borderRadius: '12px', background: form.incidentReportingEnabled ? C.blue : C.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: '12px' }}>
            <div style={{ position: 'absolute', top: '3px', left: form.incidentReportingEnabled ? '21px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </div>
        </div>

        {/* Create Login — only for existing employees */}
        {employee?.id && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px' }}>
            {loginSuccessEmail ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: C.green }}>
                <Check size={16} />
                <div style={{ fontSize: '13px', fontWeight: 600 }}>
                  Login created — {loginSuccessEmail} can now sign in.
                </div>
              </div>
            ) : !loginOpen ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>Create Login</div>
                  <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>Promote this employee to a system user — email above becomes the login, you set a temp password.</div>
                </div>
                <button type="button" onClick={() => { setLoginOpen(true); setLoginError(''); }}
                  style={{ padding: '7px 14px', borderRadius: '8px', border: `1px solid ${C.blue}55`, background: C.blue + '22', color: C.blue, fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  <UserPlus size={13} />
                  Create Login
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary, marginBottom: '4px' }}>Create Login for {form.email || '(no email)'}</div>
                <div style={{ fontSize: '11px', color: C.textMuted, marginBottom: '10px' }}>Set a temporary password — share it with the employee securely.</div>
                <input type="password" value={tempPassword} onChange={e => setTempPassword(e.target.value)}
                  placeholder="Temporary password (min 8 chars)"
                  style={{ width: '100%', boxSizing: 'border-box', background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', marginBottom: '10px' }} />
                {loginError && (
                  <div style={{ background: C.red + '22', border: `1px solid ${C.red}44`, borderRadius: '8px', padding: '8px 10px', marginBottom: '10px', fontSize: '12px', color: C.red, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <AlertCircle size={13} /> {loginError}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" onClick={handleCreateLogin} disabled={loginBusy}
                    style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: loginBusy ? C.surfaceElevated : C.blue, color: loginBusy ? C.textMuted : '#fff', fontSize: '12px', fontWeight: 700, cursor: loginBusy ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                    {loginBusy ? 'Creating…' : 'Confirm & Create'}
                  </button>
                  <button type="button" onClick={() => { setLoginOpen(false); setLoginError(''); setTempPassword(''); }} disabled={loginBusy}
                    style={{ padding: '8px 14px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ borderTop: `1px solid ${C.border}`, margin: '14px 0 16px' }} />
        <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Time & Attendance</div>
        {[
          ['taEnabled', 'Enable T&A', 'Allow this employee to clock in/out via QR'],
          ['taSelfieRequired', 'Require Selfie', 'Employee must take a selfie on clock-in'],
          ['taGeofenceRequired', 'Require Geofence', 'Clock-in is validated against site geofence'],
        ].map(([key, title, desc]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '10px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>{title}</div>
              <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>{desc}</div>
            </div>
            <div onClick={() => setForm(p => ({...p, [key]: !p[key]}))}
              style={{ width: '42px', height: '24px', borderRadius: '12px', background: form[key] ? C.blue : C.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginLeft: '12px' }}>
              <div style={{ position: 'absolute', top: '3px', left: form[key] ? '21px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
            </div>
          </div>
        ))}
        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>T&A Schedule</label>
          <select value={form.taScheduleId} onChange={e => setForm(p => ({...p, taScheduleId: e.target.value}))}
            style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: form.taScheduleId ? C.textPrimary : C.textMuted, fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}>
            <option value="">— No schedule —</option>
            {filteredSchedules.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        {filteredSchedules.find(s => s.id === form.taScheduleId)?.type === 'ROTATION' && (
          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'block', fontSize: '11px', color: C.textMuted, marginBottom: '5px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rotation Start Date</label>
            <input type="date" value={form.taRotationStart}
              onChange={e => setForm(p => ({...p, taRotationStart: e.target.value}))}
              style={{ width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 12px', color: C.textPrimary, fontSize: '13px', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '13px', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: '9px 20px', borderRadius: '8px', border: 'none', background: saving ? C.surface : C.blue, color: saving ? C.textMuted : '#fff', fontSize: '13px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{saving ? 'Saving…' : 'Save Employee'}</button>
        </div>
      </div>
    </div>
  );
}
